import { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import Pill from '../../components/common/Pill';
import CoreBadge from '../../components/common/CoreBadge';
import SecaoForm from './SecaoForm';
import SubsecaoForm from './SubsecaoForm';
import RequisitoForm from './RequisitoForm';
import { HOSPITALAR_LABELS } from '../../utils/acreditacaoOpcoes';
import { requisitosDaSubsecao } from '../../utils/catalogo';
import { onKeyActivate } from '../../utils/teclado';
import * as catalogoApi from '../../api/catalogo';

function corresponde(texto, busca) {
  return texto?.toLowerCase().includes(busca.toLowerCase());
}

export default function Catalogo() {
  const [secoes, setSecoes] = useState(null);
  const [busca, setBusca] = useState('');
  const [secoesAbertas, setSecoesAbertas] = useState(new Set());
  const [subsecoesAbertas, setSubsecoesAbertas] = useState(new Set());

  const [modalSecao, setModalSecao] = useState(false);
  const [modalSubsecao, setModalSubsecao] = useState(null); // { secao, subsecao? }
  const [modalRequisito, setModalRequisito] = useState(null); // { subsecao, requisito? }
  const [erro, setErro] = useState('');

  const carregar = async () => {
    setErro('');
    try {
      const { data } = await catalogoApi.listarHierarquia();
      setSecoes(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar o catálogo.'));
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const buscaAtiva = busca.trim().length >= 2;

  const matchSubsecao = useMemo(() => {
    if (!buscaAtiva || !secoes) return () => false;
    const cache = new Map();
    for (const secao of secoes) {
      for (const sub of secao.subsecoes) {
        const reqs = requisitosDaSubsecao(sub);
        const bate =
          corresponde(sub.numero, busca) ||
          corresponde(sub.nome, busca) ||
          reqs.some((r) => corresponde(r.codigo, busca) || corresponde(r.texto, busca));
        cache.set(sub.id_subsecao, bate);
      }
    }
    return (idSubsecao) => cache.get(idSubsecao) || false;
  }, [buscaAtiva, busca, secoes]);

  if (!secoes) return erro ? <ErroCarregamento mensagem={erro} onRetry={carregar} /> : <LoadingSpinner />;

  const toggleSecao = (id) =>
    setSecoesAbertas((s) => {
      const novo = new Set(s);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });

  const toggleSubsecao = (id) =>
    setSubsecoesAbertas((s) => {
      const novo = new Set(s);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });

  return (
    <div>
      <PageHeader
        titulo="Catálogo ONA"
        subtitulo="Seções, subseções e requisitos do Manual Brasileiro de Acreditação"
        acoes={
          <button className="am-btn am-btn-dark" onClick={() => setModalSecao(true)}>
            <span className="ms">add</span>
            Nova seção
          </button>
        }
      />

      <label className="am-field" style={{ maxWidth: 420, marginBottom: 18 }}>
        <div className="am-field__label-row">
          <span className="am-field__label">Buscar requisito</span>
        </div>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Código ou texto do requisito..."
        />
      </label>

      <div className="d-flex flex-column gap-2">
        {secoes.map((secao) => {
          const secaoAberta = secoesAbertas.has(secao.id_secao) || (buscaAtiva && secao.subsecoes.some((s) => matchSubsecao(s.id_subsecao)));
          const requisitosSecao = secao.subsecoes.flatMap(requisitosDaSubsecao);
          const totalReq = requisitosSecao.length;
          const totalCoreSecao = requisitosSecao.filter((r) => r.core).length;

          return (
            <div key={secao.id_secao} className="card-soft" style={{ overflow: 'hidden' }}>
              <button
                type="button"
                className="d-flex align-items-center gap-3 w-100 border-0 bg-transparent text-start"
                style={{ padding: '16px 20px', cursor: 'pointer' }}
                onClick={() => toggleSecao(secao.id_secao)}
              >
                <span className="ms" style={{ fontSize: 20, color: 'var(--am-cinza-texto-2)' }}>
                  {secaoAberta ? 'expand_more' : 'chevron_right'}
                </span>
                <div className="flex-grow-1">
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)' }}>
                    Seção {secao.numero} — {secao.nome}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
                    {secao.subsecoes.length} subseção(ões) · {totalReq} requisito(s) ·{' '}
                    <span style={{ color: 'var(--am-link)', fontWeight: 600 }}>{totalCoreSecao} CORE</span>
                  </div>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  className="am-btn am-btn-outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalSubsecao({ secao });
                  }}
                  onKeyDown={onKeyActivate(() => setModalSubsecao({ secao }))}
                >
                  <span className="ms">add</span>
                  Subseção
                </span>
              </button>

              {secaoAberta && (
                <div style={{ borderTop: '1px solid var(--am-borda-2)' }}>
                  {secao.subsecoes.map((sub) => {
                    const requisitos = requisitosDaSubsecao(sub);
                    const requisitosVisiveis = buscaAtiva
                      ? requisitos.filter((r) => corresponde(r.codigo, busca) || corresponde(r.texto, busca))
                      : requisitos;
                    const subAberta = subsecoesAbertas.has(sub.id_subsecao) || (buscaAtiva && matchSubsecao(sub.id_subsecao));

                    if (buscaAtiva && !matchSubsecao(sub.id_subsecao)) return null;

                    return (
                      <div key={sub.id_subsecao} style={{ borderBottom: '1px solid var(--am-borda-2)' }}>
                        <button
                          type="button"
                          className="d-flex align-items-center gap-3 w-100 border-0 bg-transparent text-start"
                          style={{ padding: '13px 20px 13px 44px', cursor: 'pointer' }}
                          onClick={() => toggleSubsecao(sub.id_subsecao)}
                        >
                          <span className="ms" style={{ fontSize: 18, color: 'var(--am-cinza-texto-2)' }}>
                            {subAberta ? 'expand_more' : 'chevron_right'}
                          </span>
                          <div className="flex-grow-1">
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--am-tinta)' }}>
                              {sub.numero} — {sub.nome}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
                              {requisitos.length} requisito(s) ·{' '}
                              <span style={{ color: 'var(--am-link)', fontWeight: 600 }}>
                                {requisitos.filter((r) => r.core).length} CORE
                              </span>
                            </div>
                          </div>
                          <Pill tone="neutral">{HOSPITALAR_LABELS[sub.hospitalar] || sub.hospitalar}</Pill>
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label={`Editar subseção ${sub.numero}`}
                            className="am-btn am-btn-outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setModalSubsecao({ secao, subsecao: sub });
                            }}
                            onKeyDown={onKeyActivate(() => setModalSubsecao({ secao, subsecao: sub }))}
                          >
                            <span className="ms">edit</span>
                          </span>
                          <span
                            role="button"
                            tabIndex={0}
                            className="am-btn am-btn-outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setModalRequisito({ subsecao: sub });
                            }}
                            onKeyDown={onKeyActivate(() => setModalRequisito({ subsecao: sub }))}
                          >
                            <span className="ms">add</span>
                            Requisito
                          </span>
                        </button>

                        {subAberta && (
                          <div className="am-table-wrap" style={{ padding: '0 20px 16px 44px' }}>
                            <table className="am-table">
                              <thead>
                                <tr>
                                  <th>Código</th>
                                  <th>Nível</th>
                                  <th>Requisito</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {requisitosVisiveis.map((r) => (
                                  <tr key={r.id_requisito}>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                      <span className="d-inline-flex align-items-center gap-1" style={{ fontWeight: 600 }}>
                                        {r.codigo}
                                        {r.core && <CoreBadge />}
                                      </span>
                                    </td>
                                    <td>{r.nivel}</td>
                                    <td style={{ maxWidth: 480 }} title={r.texto}>
                                      {r.texto}
                                      {!r.ativo && <Pill tone="outline" icon="visibility_off"> inativo</Pill>}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="am-btn am-btn-outline"
                                        aria-label={`Editar requisito ${r.codigo}`}
                                        onClick={() => setModalRequisito({ subsecao: sub, requisito: r })}
                                      >
                                        <span className="ms" aria-hidden="true">edit</span>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modalSecao && (
        <SecaoForm
          onClose={() => setModalSecao(false)}
          onSalvar={async (dados) => {
            await catalogoApi.criarSecao(dados);
            try {
              await carregar();
            } catch {
              // já salvou; só a releitura falhou
            }
          }}
        />
      )}

      {modalSubsecao && (
        <SubsecaoForm
          secao={modalSubsecao.secao}
          subsecao={modalSubsecao.subsecao}
          onClose={() => setModalSubsecao(null)}
          onSalvar={async (dados) => {
            if (modalSubsecao.subsecao) {
              await catalogoApi.atualizarSubsecao(modalSubsecao.subsecao.id_subsecao, dados);
            } else {
              await catalogoApi.criarSubsecao(dados);
              setSecoesAbertas((s) => new Set(s).add(modalSubsecao.secao.id_secao));
            }
            try {
              await carregar();
            } catch {
              // já salvou; só a releitura falhou
            }
          }}
        />
      )}

      {modalRequisito && (
        <RequisitoForm
          subsecao={modalRequisito.subsecao}
          requisito={modalRequisito.requisito}
          onClose={() => setModalRequisito(null)}
          onSalvar={async (dados) => {
            if (modalRequisito.requisito) {
              await catalogoApi.atualizarRequisito(modalRequisito.requisito.id_requisito, dados);
            } else {
              await catalogoApi.criarRequisito(dados);
              setSubsecoesAbertas((s) => new Set(s).add(modalRequisito.subsecao.id_subsecao));
            }
            try {
              await carregar();
            } catch {
              // já salvou; só a releitura falhou
            }
          }}
        />
      )}
    </div>
  );
}
